import type { PromptTemplate } from '@saaga/shared/interfaces/prompt-template.interface'
import { useGetAgencyPromptTemplatesQuery } from '@saaga/shared/store/prompt-template-endpoints.store'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { type UseFormReturn, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import type { ParsedTemplateVariable } from '@/components/organisms/template-variable-editor/template-variable.interface'
import { parseTemplateVariables } from '@/components/organisms/template-variable-editor/template-variable-parser.util'
import { deduplicateVariableList } from '@/components/organisms/template-variable-form/template-variable-deduplicator.util'
import type { TemplateVariableFormValues } from '@/components/organisms/template-variable-form/template-variable-form.interface'
import { buildTemplateVariableDefaultValues } from '@/components/organisms/template-variable-form/template-variable-schema-builder.util'
import { buildAgencyDeepLink } from '@/lib/deep-link-intent.util'

interface UseDeepLinkBuilderParams {
  agencySlug: string
  isOpen: boolean
}

export interface UseDeepLinkBuilderReturn {
  templateList: PromptTemplate[]
  selectedTemplateId: string | null
  onTemplateSelect: (templateId: string | null) => void
  parsedVariableList: ParsedTemplateVariable[]
  form: UseFormReturn<TemplateVariableFormValues>
  urlPreview: string
  onCopy: () => void
  isLoading: boolean
}

const toStringValues = (values: TemplateVariableFormValues): Record<string, string> => {
  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === null) continue
    const text = String(value)
    if (text.length === 0) continue
    result[key] = text
  }
  return result
}

const writeToClipboard = async (link: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(link)
    toast.success('Link copied to clipboard')
  } catch (error) {
    logger.warn('[DeepLinkBuilder] Failed to copy link', error)
    toast.error('Failed to copy link')
  }
}

const useAgencyTemplateList = (
  agencySlug: string,
  isOpen: boolean
): { templateList: PromptTemplate[]; isFetching: boolean } => {
  const { data, isFetching } = useGetAgencyPromptTemplatesQuery(
    { agencySlug },
    { skip: !isOpen || !agencySlug }
  )
  const templateList = useMemo(() => data?.itemList ?? [], [data])
  return { templateList, isFetching }
}

const useSelectedTemplateVariables = (
  templateList: PromptTemplate[],
  selectedTemplateId: string | null
): ParsedTemplateVariable[] => {
  const selectedTemplate = useMemo(
    () => templateList.find((template) => template.id === selectedTemplateId) ?? null,
    [templateList, selectedTemplateId]
  )
  return useMemo(
    () => (selectedTemplate ? parseTemplateVariables(selectedTemplate.content) : []),
    [selectedTemplate]
  )
}

const useOptionalVariableForm = (
  parsedVariableList: ParsedTemplateVariable[]
): {
  form: UseFormReturn<TemplateVariableFormValues>
  uniqueVariableList: ParsedTemplateVariable[]
} => {
  const uniqueVariableList = useMemo(
    () => deduplicateVariableList(parsedVariableList),
    [parsedVariableList]
  )
  const defaultValues = useMemo(
    () => buildTemplateVariableDefaultValues(uniqueVariableList),
    [uniqueVariableList]
  )
  const form = useForm<TemplateVariableFormValues>({ defaultValues })
  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])
  return { form, uniqueVariableList }
}

export const useDeepLinkBuilder = ({
  agencySlug,
  isOpen,
}: UseDeepLinkBuilderParams): UseDeepLinkBuilderReturn => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const { templateList, isFetching } = useAgencyTemplateList(agencySlug, isOpen)
  const parsedVariableList = useSelectedTemplateVariables(templateList, selectedTemplateId)
  const { form } = useOptionalVariableForm(parsedVariableList)
  const watchedValues = form.watch()

  useEffect(() => {
    if (!isOpen) return
    setSelectedTemplateId(null)
    form.reset({})
  }, [isOpen, form])

  const urlPreview = useMemo(
    () =>
      buildAgencyDeepLink(agencySlug, {
        templateId: selectedTemplateId ?? undefined,
        prefillValues: toStringValues(watchedValues),
      }),
    [agencySlug, selectedTemplateId, watchedValues]
  )

  const onCopy = useCallback(() => {
    const link = buildAgencyDeepLink(agencySlug, {
      templateId: selectedTemplateId ?? undefined,
      prefillValues: toStringValues(form.getValues()),
    })
    void writeToClipboard(link)
  }, [agencySlug, selectedTemplateId, form])

  return {
    templateList,
    selectedTemplateId,
    onTemplateSelect: setSelectedTemplateId,
    parsedVariableList,
    form,
    urlPreview,
    onCopy,
    isLoading: isFetching,
  }
}
